<template>
  <div id="accordionTourNotInclude" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTourNotInclude" class="accordion-header">
        <button
          aria-controls="collapseTourNotInclude"
          aria-expanded="true"
          class="accordion-button collapsed w-100 d-flex justify-content-between align-items-center"
          data-bs-target="#collapseTourNotInclude"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
              <v-icon icon="contact" size="xs" class="text-white" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.notInclude") }}</div>
            </div>
          </div>
          <div class="tooltip-wrapper">
            <v-icon icon="info" size="xs" space="p-4" class="text-secondary" />
            <div class="tooltip">Hello World</div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTourNotInclude"
        aria-labelledby="headingTourNotInclude"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTourNotInclude"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <nav class="translations-tab mb-8">
            <div id="nav-tab" class="nav nav-tabs border-bottom-0" role="tablist">
              <v-button
                v-for="(language, index) in languages"
                :id="`nav-template-day-update-basic-information-${language.key}`"
                :key="index"
                :aria-controls="`template-day-update-basic-information-${language.key}`"
                :class="{ active: language.shortName === currentLanguage }"
                :data-bs-target="`#template-day-update-basic-information-${language.key}`"
                aria-selected="true"
                class="nav-tab-link py-4 px-12 me-8 text-xs"
                data-bs-toggle="tab"
                role="tab"
                size="sm"
                variant="outline-primary"
                @click="currentLanguage = language.shortName"
              >
                {{ language.label }}
              </v-button>
            </div>
          </nav>
          <form id="case-tour-update-not-include-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset v-for="language in languages" :key="language.shortName">
              <div class="form-outline" v-if="currentLanguage === language.shortName">
                <label class="label text-xs fw-bold text-uppercase text-muted mb-2">
                  <span>{{ $t("clientTour.notInclude") }}</span>
                </label>
                <v-html-editor
                  :id="'case-tour-update-not-include-' + language.shortName"
                  :ref="'editor_not_include_' + language.shortName"
                  :value="notInclude[language.shortName].description"
                  @input="(notInclude[language.shortName].description = $event), isSaveButtonDisabled"
                  @media-uploaded="editorMedia.push($event)"
                  height="300"
                />
              </div>
            </fieldset>
            <div class="d-flex gap-4">
              <v-button class="mt-8 variable-badge" size="sm" variant="outline-primary" @click="addNotIncludeInEditor('[no_of_days]')"> no_of_days </v-button>
              <v-button class="mt-8 variable-badge" size="sm" variant="outline-primary" @click="addNotIncludeInEditor('[no_of_nights]')"> no_of_nights </v-button>
            </div>

            <v-button
              :is-loading="accepted"
              :disabled="isSaveButtonDisabled"
              class="btn-icon w-fit align-self-end mt-12"
              size="xs"
              variant="primary"
              @click="updateTour"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="xs" space="ms-8" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, getCurrentInstance, nextTick } from "vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import api from "@/api";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import { useAppLanguages, useLocaleKeyedRef } from "@/composables/useAppLanguages";
import ClientTour from "@/api/objects/ClientTour";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";

const props = defineProps<{
  agency: string;
  clientTour: ClientTour;
  caseId: number;
}>();

const emit = defineEmits<{
  (e: "reloadCase"): void;
}>();

const { t } = useI18n();
const toast = useToast();
const { proxy } = getCurrentInstance(); 
const notIncludeData = props.clientTour?.excluded_trip_information?.translations || [];

const notInclude = useLocaleKeyedRef(() => ({ description: "" }));
const disableButton = ref(false);
const editorMedia = ref<any[]>([]);
const { languages, resolveStoredLocale } = useAppLanguages();
const currentLanguage = ref(resolveStoredLocale(localStorage.getItem("language")));

notIncludeData.forEach((translation: { locale: string; description: string }) => {
  const { locale, description } = translation;
  if (notInclude.value.hasOwnProperty(locale)) {
    notInclude.value[locale].description = description;
  }
});

const form = useFormValidation("", notInclude);
const { accepted, toggleAccepted } = form;

const isSaveButtonDisabled = computed(() => {
  return !Object.keys(notInclude.value).some((locale) => {
    return notInclude.value[locale]?.description?.trim() !== "";
  });
});

const addNotIncludeInEditor = (text: string) => {
  nextTick(() => {
    const refName = 'editor_not_include_' + currentLanguage.value;
    let editorComponent = proxy?.$refs?.[refName];

    if (Array.isArray(editorComponent)) {
      editorComponent = editorComponent[0];
    }
    if (!editorComponent) {
      console.warn('Editor not found:', refName);
      return;
    }
    editorComponent.insertText(text);
  });
};

const updateTour = async () => {
  try {
    toggleAccepted();
    disableButton.value = true;

    const data = {
      not_included_information: notInclude.value,
    };

    await api.case.updateTranslations(props.agency, props.caseId, data);

    if (editorMedia.value.length > 0) {
      const mediaPayload = {
        media: editorMedia.value,
        model_type: "client_tour",
        collection: "tinymce",
        model_id: props.clientTour.id,
      };

      await api.media.confirm("s3", mediaPayload);
    }

    toast.success(t("general.shared.savedChanges"));
    emit("reloadCase");
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || "Something went wrong");
  } finally {
    disableButton.value = false;
    toggleAccepted();
  }
};
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.8rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.75rem !important;
}

:deep(.v-form-input),
:deep(.v-form) {
  margin-bottom: 0.25rem !important;
}

.tooltip-wrapper {
  position: relative;
  display: inline-block;
}

.tooltip {
  position: absolute;
  bottom: 100%; /* Adjust tooltip position */
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--uq-primary);
  color: #fff;
  padding: 5px 10px;
  border-radius: 5px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out;
}

.tooltip-wrapper:hover .tooltip {
  opacity: 1;
  visibility: visible;
}

.variable-badge {
  font-size: 0.55rem !important;
  padding: 1px 6px !important;
  border-radius: 4px !important;
  height: 25px !important;
  min-height: 25px !important;
  line-height: 1 !important;
  background-color: #E2C92E !important;
  color: #fff !important;
  border: none !important;
}
</style>
